import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Zap, Clock3, Send, Users, CalendarDays, RefreshCw } from 'lucide-react';

const initialFormState = {
  title: '',
  shift_date: '',
  start_time: '19:00',
  hourly_rate: '',
  city: '',
  state: '',
  max_invites: 10,
  radius_miles: 25,
  available_tonight_only: true,
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const InstantShiftFillCard = ({ onShiftCreated }) => {
  const [form, setForm] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsRefreshing, setMetricsRefreshing] = useState(false);
  const [metrics, setMetrics] = useState({
    kpis: {
      shifts_posted_24h: 0,
      invites_sent_24h: 0,
      applications_received_24h: 0,
    },
    recent_shifts: [],
  });

  const fetchMetrics = async ({ showLoadingState = true } = {}) => {
    try {
      if (showLoadingState) {
        setMetricsLoading(true);
      } else {
        setMetricsRefreshing(true);
      }

      const response = await axios.get('/owner/instant-shift-fill/metrics');
      setMetrics(response.data || { kpis: {}, recent_shifts: [] });
    } catch (error) {
      console.error('Failed to fetch instant shift metrics:', error);
      toast.error('Could not load instant shift metrics');
    } finally {
      if (showLoadingState) {
        setMetricsLoading(false);
      } else {
        setMetricsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const hourlyRate = Number(form.hourly_rate);
      const radiusMiles = Number(form.radius_miles);
      const maxInvites = Number(form.max_invites);

      if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) {
        toast.error('Please enter a valid hourly rate greater than 0.');
        return;
      }

      if (!Number.isFinite(radiusMiles) || radiusMiles < 1 || radiusMiles > 100) {
        toast.error('Radius must be between 1 and 100 miles.');
        return;
      }

      if (!Number.isFinite(maxInvites) || maxInvites < 1 || maxInvites > 50) {
        toast.error('Max invites must be between 1 and 50.');
        return;
      }

      const response = await axios.post('/owner/instant-shift-fill', {
        ...form,
        hourly_rate: hourlyRate,
        max_invites: maxInvites,
        radius_miles: radiusMiles,
      });

      const invitedNow = response.data?.invited_now || 0;
      const totalInvited = response.data?.total_invited || 0;
      toast.success(`Shift posted. ${invitedNow} invites sent (${totalInvited} total invited).`);

      setForm((prev) => ({
        ...initialFormState,
        city: prev.city,
        state: prev.state,
      }));

      await Promise.all([
        fetchMetrics({ showLoadingState: false }),
        onShiftCreated ? onShiftCreated() : Promise.resolve(),
      ]);
    } catch (error) {
      console.error('Failed to create instant shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to create instant shift');
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled =
    submitting ||
    !form.title.trim() ||
    !form.shift_date ||
    !form.start_time ||
    !form.city.trim() ||
    !form.state.trim() ||
    Number(form.hourly_rate) <= 0;

  return (
    <Card className="bg-white/5 backdrop-blur-md border border-primary/20 mb-8" data-testid="instant-shift-fill-card">
      <CardHeader className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="font-['Barlow_Condensed'] uppercase flex items-center" data-testid="instant-shift-fill-title">
              <Zap className="h-5 w-5 mr-2 text-primary" />
              Instant Shift Fill
            </CardTitle>
            <CardDescription data-testid="instant-shift-fill-description">
              Post a shift in under 60 seconds and auto-invite nearby bartenders.
            </CardDescription>
          </div>

          <div className="flex items-center gap-2" data-testid="instant-shift-fill-header-actions">
            {metricsRefreshing && (
              <Badge variant="outline" className="text-xs" data-testid="instant-shift-refreshing-badge">
                Syncing...
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchMetrics({ showLoadingState: false })}
              disabled={metricsRefreshing || submitting}
              data-testid="instant-shift-refresh-metrics-btn"
            >
              <RefreshCw className="h-3 w-3 mr-1" />Refresh
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3" data-testid="instant-shift-fill-cta-banner">
          <p className="text-sm text-white" data-testid="instant-shift-fill-cta-banner-text">
            Need coverage tonight? Use this to auto-blast invites to nearby bartenders who are available now.
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <form className="space-y-4" onSubmit={handleSubmit} data-testid="instant-shift-fill-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="instant-shift-title" data-testid="instant-shift-title-label">Shift title</Label>
              <Input
                id="instant-shift-title"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Tonight Barback + Bartender Coverage"
                required
                data-testid="instant-shift-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instant-shift-date" data-testid="instant-shift-date-label">Date</Label>
              <Input
                id="instant-shift-date"
                type="date"
                value={form.shift_date}
                onChange={(event) => setForm((prev) => ({ ...prev, shift_date: event.target.value }))}
                required
                data-testid="instant-shift-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instant-shift-time" data-testid="instant-shift-time-label">Start time</Label>
              <Input
                id="instant-shift-time"
                type="time"
                value={form.start_time}
                onChange={(event) => setForm((prev) => ({ ...prev, start_time: event.target.value }))}
                required
                data-testid="instant-shift-time-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instant-shift-rate" data-testid="instant-shift-rate-label">Hourly rate ($)</Label>
              <Input
                id="instant-shift-rate"
                type="number"
                min="1"
                step="0.5"
                value={form.hourly_rate}
                onChange={(event) => setForm((prev) => ({ ...prev, hourly_rate: event.target.value }))}
                required
                data-testid="instant-shift-rate-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instant-shift-city" data-testid="instant-shift-city-label">City</Label>
              <Input
                id="instant-shift-city"
                value={form.city}
                onChange={(event) => setForm((prev) => ({ ...prev, city: event.target.value }))}
                placeholder="Youngstown"
                required
                data-testid="instant-shift-city-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instant-shift-state" data-testid="instant-shift-state-label">State</Label>
              <Input
                id="instant-shift-state"
                value={form.state}
                onChange={(event) => setForm((prev) => ({ ...prev, state: event.target.value }))}
                placeholder="OH"
                required
                data-testid="instant-shift-state-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instant-shift-radius" data-testid="instant-shift-radius-label">Radius (miles)</Label>
              <Input
                id="instant-shift-radius"
                type="number"
                min="1"
                max="100"
                value={form.radius_miles}
                onChange={(event) => setForm((prev) => ({ ...prev, radius_miles: event.target.value }))}
                data-testid="instant-shift-radius-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="instant-shift-max-invites" data-testid="instant-shift-max-invites-label">Max invites</Label>
              <Input
                id="instant-shift-max-invites"
                type="number"
                min="1"
                max="50"
                value={form.max_invites}
                onChange={(event) => setForm((prev) => ({ ...prev, max_invites: event.target.value }))}
                data-testid="instant-shift-max-invites-input"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-white/10 p-3" data-testid="instant-shift-availability-row">
            <div>
              <p className="text-sm font-medium text-white" data-testid="instant-shift-availability-title">Invite only bartenders available tonight</p>
              <p className="text-xs text-muted-foreground" data-testid="instant-shift-availability-subtitle">Higher response rate for urgent coverage</p>
            </div>
            <Switch
              checked={form.available_tonight_only}
              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, available_tonight_only: checked }))}
              data-testid="instant-shift-availability-switch"
            />
          </div>

          <Button
            type="submit"
            disabled={isSubmitDisabled}
            className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))]"
            data-testid="instant-shift-submit-button"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? 'Posting Shift...' : 'Post & Auto-Invite'}
          </Button>
        </form>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="instant-shift-kpi-grid">
          <div className="rounded-lg border border-white/10 p-3" data-testid="instant-shift-kpi-shifts-24h-card">
            <p className="text-xs text-muted-foreground">Shifts Posted (24h)</p>
            <p className="text-xl font-semibold text-white" data-testid="instant-shift-kpi-shifts-24h-value">
              {metricsLoading ? <span className="inline-block h-6 w-10 rounded bg-white/10 animate-pulse" data-testid="instant-shift-kpi-shifts-loading" /> : metrics?.kpis?.shifts_posted_24h ?? 0}
            </p>
          </div>
          <div className="rounded-lg border border-white/10 p-3" data-testid="instant-shift-kpi-invites-24h-card">
            <p className="text-xs text-muted-foreground">Invites Sent (24h)</p>
            <p className="text-xl font-semibold text-white" data-testid="instant-shift-kpi-invites-24h-value">
              {metricsLoading ? <span className="inline-block h-6 w-10 rounded bg-white/10 animate-pulse" data-testid="instant-shift-kpi-invites-loading" /> : metrics?.kpis?.invites_sent_24h ?? 0}
            </p>
          </div>
          <div className="rounded-lg border border-white/10 p-3" data-testid="instant-shift-kpi-applications-24h-card">
            <p className="text-xs text-muted-foreground">Applications (24h)</p>
            <p className="text-xl font-semibold text-white" data-testid="instant-shift-kpi-applications-24h-value">
              {metricsLoading ? <span className="inline-block h-6 w-10 rounded bg-white/10 animate-pulse" data-testid="instant-shift-kpi-applications-loading" /> : metrics?.kpis?.applications_received_24h ?? 0}
            </p>
          </div>
        </div>

        <div className="space-y-2" data-testid="instant-shift-recent-list-section">
          <div className="flex items-center gap-2" data-testid="instant-shift-recent-header">
            <Clock3 className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium text-white">Recent Shift Posts</p>
          </div>

          {(metrics?.recent_shifts || []).slice(0, 3).map((shift) => (
            <div className="rounded-md border border-white/10 p-3 flex items-center justify-between gap-3" key={shift.id} data-testid={`instant-shift-recent-item-${shift.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate" data-testid={`instant-shift-recent-title-${shift.id}`}>{shift.title}</p>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-1">
                  <span className="inline-flex items-center" data-testid={`instant-shift-recent-date-${shift.id}`}><CalendarDays className="h-3 w-3 mr-1" />{formatDate(shift.start_date || shift.created_at)}</span>
                  <span className="inline-flex items-center" data-testid={`instant-shift-recent-invites-${shift.id}`}><Users className="h-3 w-3 mr-1" />{shift.invited_count || 0} invited</span>
                </div>
              </div>
              <Badge variant="outline" data-testid={`instant-shift-recent-status-${shift.id}`}>{shift.status || 'open'}</Badge>
            </div>
          ))}

          {!metricsLoading && (!metrics?.recent_shifts || metrics.recent_shifts.length === 0) && (
            <div className="rounded-md border border-dashed border-white/10 p-3 text-sm text-muted-foreground" data-testid="instant-shift-recent-empty-state">
              No shift posts yet. Create one above to start auto-inviting bartenders.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
